import type { components } from "#open-fetch-schemas/atlit";
import type { IOrganization, IRequestPayloadCreateOrganization, IRequestPayloadOrganization, ITenantOrganization } from "~/types";
import { ORGANIZATION_LIVE_EVENTS, ORGANIZATION_TYPES } from "~/constants";

import type { TOrganizationSchema } from "../schemas/organization.schema";

export default function formatOrganization(organization: components["schemas"]["Organization"]): IOrganization {
  const tenant: ITenantOrganization | null = organization?.relationships?.ancestors
    ? {
        id: organization.relationships.ancestors.id,
        name: organization.relationships.ancestors.attributes.name,
        name_short: organization.relationships.ancestors.attributes.shortName ?? "",
        logo_url: organization.relationships.ancestors.attributes.logo ?? "",
        location: organization.relationships.ancestors.attributes.location,
      }
    : null;

  return {
    id: organization.id,
    name: organization.attributes.name,
    name_short: organization.attributes.shortName,
    logo_url: organization.attributes.logo,
    organization_type: organization.attributes.type,
    location: {
      country: organization.attributes.location?.country,
      state: organization.attributes.location?.state,
      city: organization.attributes.location?.city,
    },
    tenant,
    external: organization.attributes.external,
    totalChildOrganizations: organization.relationships?.membersCount,
    permissions: {
      canRead: organization.meta?.permissions?.includes("read") || false,
      canWrite: organization.meta?.permissions?.includes("write") || false,
      canInviteAdmin: organization.meta?.permissions?.includes("members:invite:admin") || false,
      canRemoveAdmin: organization.meta?.permissions?.includes("members:remove:admin") || false,
      canInviteTeam: organization.meta?.permissions?.includes("team:invite") || false,
      canRemoveTeam: organization.meta?.permissions?.includes("team:remove") || false,
      canDelete: organization.meta?.permissions?.includes("delete") || false,
    },
    ancestors: organization.attributes?.ancestors,
    isDisabledSharingEvents: organization.attributes?.rules?.sharing?.type?.includes("private") || false,
    isDisabledLiveEvents: organization.relationships?.orgConfig?.attributes?.events?.live?.status === ORGANIZATION_LIVE_EVENTS.DISABLED || false,
  };
}

export function formatOrganizationCreatePayload(payload: TOrganizationSchema): IRequestPayloadCreateOrganization {
  return {
    name: payload.name,
    shortName: payload.name_short || undefined,
    logo: payload.logo_file_key || undefined,
    type: payload.organization_type,
    location: {
      // @ts-expect-error country is optional
      country: payload.location?.country || undefined,
      state: payload.location?.state || undefined,
      // @ts-expect-error city is optional
      city: payload.location?.city || undefined,
    },
    rules: {
      sharing: {
        type: payload.isDisabledSharingEvents ? "private" : "public",
      },
    },
    parent: payload.parent || undefined,
  };
}

export function formatOrganizationPayload(organization: Partial<TOrganizationSchema>): Partial<IRequestPayloadOrganization> {
  const payload: Partial<IRequestPayloadOrganization> = {
    name: organization.name,
    shortName: organization.name_short,
    logo: organization.logo_file_key,
    // @ts-expect-error Should be updated on Atlit BE swagger
    type: organization.organization_type,
  };

  if (organization.location) {
    payload.location = {
      country: organization.location?.country,
      state: organization.location?.state || null,
      city: organization.location?.city,
    };
  }

  if (organization.isDisabledSharingEvents !== undefined) {
    payload.rules = {
      sharing: {
        type: organization.isDisabledSharingEvents ? "private" : "public",
      },
    };
  }

  if (organization.isDisabledLiveEvents !== undefined) {
    payload.config = {
      blockLiveEvents: organization.isDisabledLiveEvents,
    };
  }

  if (organization.logo_url === null) {
    payload.logo = null;
  }

  return payload;
}

export function formatUnassignedOrganization(organization: IOrganization, totalOrganizations: number): IOrganization {
  const baseURL = useRuntimeConfig().app.baseURL;
  return {
    ...organization,
    name: "Unassigned",
    organization_type: "tenant",
    logo_url: `${baseURL}images/no-organization-logo.svg`,
    totalChildOrganizations: totalOrganizations,
    id: "unassigned",
    location: {
      country: null,
      state: null,
      city: null,
    },
  };
}

export function getUnassignedOrganization(): IOrganization {
  const baseURL = useRuntimeConfig().app.baseURL;
  const user = useUser();
  const isSuperAdmin = user.value?.hasSuperAdminRole;

  return {
    name: "Unassigned",
    organization_type: "tenant",
    logo_url: `${baseURL}images/no-organization-logo.svg`,
    id: "unassigned",
    permissions: {
      canRead: !isSuperAdmin,
      canWrite: !isSuperAdmin,
      canInviteAdmin: !isSuperAdmin,
      canRemoveAdmin: !isSuperAdmin,
      canInviteTeam: !isSuperAdmin,
      canRemoveTeam: !isSuperAdmin,
      canDelete: !isSuperAdmin,
    },
    isDisabledSharingEvents: false,
    isDisabledLiveEvents: false,
    location: {
      country: null,
      state: null,
      city: null,
    },
  };
}

/**
 * Check if an organization type is a high school organization.
 * Only high school organizations should display level-related UI elements.
 *
 * @param organizationType The organization type string
 * @returns true if the organization is a high school, false otherwise
 */
export function isHighSchoolOrganization(organizationType?: string): boolean {
  return organizationType === ORGANIZATION_TYPES.SCHOOL;
}
